import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCertificateTemplateVersionById, updateCertificateTemplateVersion } from "../../../../../redux/features/certificateTemplateVersion/certificateTemplateVersionService";
import { useDispatch, useSelector } from "react-redux";
import styles from '../styles.module.css';
import { getCertificateTemplateVersion, STATUS } from "../../../../../redux/features/certificateTemplateVersion/certificateTemplateVersionSlice";
import GridLoader from "react-spinners/GridLoader";
import BeatLoader from "react-spinners/BeatLoader";
import { showToast } from "../../../../../components/ToastMsg";

const EditCertificateTemplateVersion = () => {
    const { versionId } = useParams();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { status: templateVersionStatus, data: certificateTemplateVersion } = useSelector(getCertificateTemplateVersion);
    const isCertificateTemplateVersionLoading = templateVersionStatus === STATUS.LOADING;
    const isCertificateVersionTemplateLoaded = templateVersionStatus === STATUS.SUCCEEDED;
    const isCertificateVersionTemplateFailed = templateVersionStatus === STATUS.FAILED;

    const [formData, setFormData] = useState({});
    const [initialData, setInitialData] = useState(null);
    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);

    useEffect(() => {
        if (versionId) {
            dispatch(getCertificateTemplateVersionById({ versionId }));
        }
        return () => {
            dispatch({
                type: 'certificateTemplateVersion/certificateTemplateVersionDetails/reset',
            });
        };
    }, [dispatch, versionId]);

    useEffect(() => {
        if (!versionId) return; // skip if no edit ID

        if (isCertificateVersionTemplateLoaded && certificateTemplateVersion) {
            setFormData(certificateTemplateVersion);
            setInitialData(certificateTemplateVersion);
        }

        if (isCertificateVersionTemplateFailed) {
            navigate('/admin/certificate-template', { replace: true });
        }
    }, [templateVersionStatus, certificateTemplateVersion, versionId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialData[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const handleUpdate = async () => {
        try {
            const data = getChangedValues(formData);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                showToast('error', 'There are no changes to update.');
            } else {
                setBtnDisable(true);
                const response = await dispatch(updateCertificateTemplateVersion(data)).unwrap();
                dispatch(getCertificateTemplateVersionById({ versionId }));
                showToast('success', response.message);
                setBtnDisable(false);
            }
        } catch (error) {
            setBtnDisable(false);
            showToast('error', error.message);
        }
    };

    return (
        <div className="main-card">
            <h1 className="form-title">Edit Certificate Template Version</h1>
            <div className={styles.formContainer}>
                {isCertificateTemplateVersionLoading ? (
                    <div className={`loader-style`}>
                        <GridLoader color='var(--primary)' />
                    </div>
                ) : (
                    <form className={styles.coordinatorForm} noValidate>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>
                                        Version Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name || ""}
                                        onChange={handleInputChange}
                                        placeholder="Enter Version (e.g., v1.0.0)"
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Certificate Phrase 1</label>
                                    <input
                                        type="text"
                                        name="certificate_phrase_1"
                                        value={formData.certificate_phrase_1 || ""}
                                        onChange={handleInputChange}
                                        placeholder="CERTIFICATE OF RECOGNITION"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Certificate Phrase 2</label>
                                    <input
                                        type="text"
                                        name="certificate_phrase_2"
                                        value={formData.certificate_phrase_2 || ""}
                                        onChange={handleInputChange}
                                        placeholder="THIS IS TO CERTIFY THAT"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Certificate Phrase 3</label>
                                    <input
                                        type="text"
                                        name="certificate_phrase_3"
                                        value={formData.certificate_phrase_3 || ""}
                                        onChange={handleInputChange}
                                        placeholder="is hereby recognized for"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div><div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Conducted By Phrase</label>
                                    <input
                                        type="text"
                                        name="conducted_by_phrase"
                                        value={formData.conducted_by_phrase || ""}
                                        onChange={handleInputChange}
                                        placeholder="Conducted by"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Completion Phrase</label>
                                    <input
                                        type="text"
                                        name="completion_phrase"
                                        value={formData.completion_phrase || ""}
                                        onChange={handleInputChange}
                                        placeholder="Completed on"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* 
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Conducted By</label>
                                    <input
                                        type="text"
                                        name="conducted_by"
                                        value={formData.conducted_by || ""}
                                        onChange={handleInputChange}
                                        placeholder="School of Accounting and Finance (SAFSA), IBSUniversity"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Name 1</label>
                                    <input
                                        type="text"
                                        name="signature_name_1"
                                        value={formData.signature_name_1 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Prof. Ramachandran Arunachalam"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div> */}
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Designation 1</label>
                                    <input
                                        type="text"
                                        name="signature_designation_1"
                                        value={formData.signature_designation_1 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Vice Chancellor"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Name 2</label>
                                    <input
                                        type="text"
                                        name="signature_name_2"
                                        value={formData.signature_name_2 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Dr. Arumugam Mourougane"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div> */}
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label className={`form-label`}>Signature Designation 2</label>
                                    <input
                                        type="text"
                                        name="signature_designation_2"
                                        value={formData.signature_designation_2 || ""}
                                        onChange={handleInputChange}
                                        placeholder="Head of School - SOAF"
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            onClick={handleUpdate}
                            disabled={disabledBtn}>
                            {disabledBtn ? (
                                <BeatLoader grade='#ffffff' />
                            ) : (
                                `Save`
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditCertificateTemplateVersion;